/**
 * Komal OS - CRM: bulk-import an existing contacts spreadsheet.
 *
 * Row-level partial success (see app/routes/crm_routes.py's
 * import_contacts) - unlike this app's other bulk imports (expenses,
 * punch points), a bad row here doesn't block the good ones. This page's
 * job is mainly to show that outcome clearly per row, not just a single
 * pass/fail toast.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, UploadCloud, Loader2, CheckCircle2, AlertTriangle, ArrowLeft } from 'lucide-react';
import { importContacts, downloadContactsImportTemplate } from '../../services/crmService';
import { downloadBlob } from '../../utils/downloadFile';
import useUIStore from '../../store/uiStore';

export default function ImportContactsPage() {
  const { showToast } = useUIStore();
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleTemplate = async () => {
    try {
      const res = await downloadContactsImportTemplate();
      downloadBlob(res.data, 'crm_contacts_import_template.csv');
    } catch (err) {
      showToast('Could not download the template', 'error');
    }
  };

  const handleFile = (chosen) => {
    if (!chosen) return;
    if (!chosen.name.toLowerCase().endsWith('.csv')) {
      showToast('Please choose a .csv file', 'error');
      return;
    }
    setFile(chosen);
    setResult(null);
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const res = await importContacts(file);
      setResult(res.data);
      if (res.data.contacts_created > 0) {
        showToast(`Imported ${res.data.contacts_created} contact${res.data.contacts_created === 1 ? '' : 's'}`, 'success');
      }
    } catch (err) {
      showToast(err.response?.data?.detail || 'Import failed', 'error');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-2xl mx-auto">
        <Link to="/crm/accounts" className="flex items-center gap-1.5 text-xs text-white/70 hover:text-white mb-3">
          <ArrowLeft size={13} /> Customers
        </Link>
        <div className="mb-6">
          <h1 className="text-xl font-bold text-white">Import existing contacts</h1>
          <p className="text-sm text-white/70 mt-0.5">Bring in a spreadsheet of contacts you already have — one row per person.</p>
        </div>

        <div className="glass-card p-5 mb-5">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="text-sm text-gray-600">
              <p className="font-semibold text-gray-800 mb-1">1. Start from the template</p>
              <p>Columns: <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">company, name, title, phone, email, country, website, industry_notes, event, owner_email</code>. Only <b>company</b> and <b>name</b> are required — leave the rest blank if you don't have it.</p>
            </div>
            <button onClick={handleTemplate} className="flex items-center gap-1.5 text-sm font-semibold text-komal-blue border border-gray-300 rounded-lg px-3 py-2 shrink-0 hover:bg-gray-50">
              <Download size={14} /> Template
            </button>
          </div>

          <p className="text-sm font-semibold text-gray-800 mb-2">2. Upload your filled-in CSV</p>
          <label
            htmlFor="crm-contacts-csv"
            className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 hover:border-komal-blue rounded-xl py-8 cursor-pointer transition-colors"
          >
            <UploadCloud size={24} className="text-gray-400 mb-2" />
            <span className="text-sm text-gray-600">{file ? file.name : 'Click to choose a .csv file, or drop one here'}</span>
          </label>
          <input id="crm-contacts-csv" type="file" accept=".csv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />

          {file && (
            <button
              onClick={handleImport}
              disabled={importing}
              className="w-full mt-4 flex items-center justify-center gap-2 text-sm font-semibold text-white bg-komal-blue rounded-lg px-3 py-2.5 disabled:opacity-60"
            >
              {importing ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />}
              {importing ? 'Importing…' : 'Import contacts'}
            </button>
          )}
        </div>

        {result && (
          <div className="glass-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 size={16} className="text-green-600" />
              <span className="text-sm font-semibold text-gray-800">Import finished</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-lg font-extrabold text-gray-800 tabular-nums">{result.rows_total}</div>
                <div className="text-[11px] text-gray-500 uppercase tracking-wide">Rows read</div>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <div className="text-lg font-extrabold text-green-700 tabular-nums">{result.contacts_created}</div>
                <div className="text-[11px] text-gray-500 uppercase tracking-wide">Contacts added</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <div className="text-lg font-extrabold text-blue-700 tabular-nums">{result.accounts_created}</div>
                <div className="text-[11px] text-gray-500 uppercase tracking-wide">New customers</div>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-center">
                <div className="text-lg font-extrabold text-amber-700 tabular-nums">{result.duplicates_flagged}</div>
                <div className="text-[11px] text-gray-500 uppercase tracking-wide">Flagged as duplicate</div>
              </div>
            </div>

            {result.duplicates_flagged > 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
                {result.duplicates_flagged} row{result.duplicates_flagged === 1 ? '' : 's'} matched an existing contact by email or phone — both were kept rather than merged automatically. Check the matching customer's contacts to reconcile.
              </p>
            )}

            {result.errors.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 text-sm font-semibold text-red-700 mb-2">
                  <AlertTriangle size={14} /> {result.errors.length} row{result.errors.length === 1 ? '' : 's'} skipped
                </div>
                <ul className="text-xs text-gray-600 divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                  {result.errors.map((e, i) => (
                    <li key={i} className="px-3 py-2 flex gap-2">
                      <span className="font-semibold text-gray-400 shrink-0">Row {e.row}</span>
                      <span>{e.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Link to="/crm/accounts" className="inline-block mt-4 text-sm font-semibold text-komal-blue hover:underline">
              View imported customers →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
